import {
  Client,
  clientContactSchema,
  ClientSchema,
  clientSettingsSchema,
  Contact,
  emailSchema,
  idSchema,
  legalEntitySchema,
  nameSchema,
  objectIdSchema,
  optionalSparseSchema,
  optionalStringSchema,
  paginationWithSearchSchema,
  partyRoleSchema,
  partySchema,
  phoneSchema,
} from '@hero/schema';
import { API } from '..';
import { z } from 'zod';

const schema = ClientSchema.model;

/**
 * Client API DTOs.
 *
 * `Client` is the unified CRM entity (per-Org ref). Roles are
 * **multi-valued** — a single `Client` doc may carry
 * `business_customer`, `carrier_company`, `broker`, and/or
 * `individual_customer` simultaneously. The Hero platform is a network,
 * not a marketplace, so any party can be on either side of any
 * relationship (see `.cursorrules`).
 *
 * Replaces the legacy `customer.api.ts`.
 */

// * get all api
export const clientGetAllSchemaAPI = paginationWithSearchSchema.extend({
  role: partyRoleSchema
    .optional()
    .describe(
      "Filter by a single role — returns Clients whose `roles[]` contains this value. A Client can carry multiple roles ('business_customer', 'carrier_company', 'broker', 'individual_customer'); this picks one to narrow on.",
    ),
});
export type ClientGetAllAPI = API<z.infer<typeof clientGetAllSchemaAPI>, Client[]>;

//* create api
//
// `clientName` + `roles` are the only requirements — see
// `ClientSchema.primaryContact` for why the contact is enrichment rather
// than identity. Callers that DO know a person (the web create modal,
// the Hero agent) still send all three person fields; the ones that
// legitimately don't (a hauling company added purely so its CR can be
// declared on a waybill) send none.
export const clientCreateAPISchema = z.object({
  /**
   * Piped through a strict `z.string()` before `nameSchema` on purpose.
   * `nameSchema` is `z.coerce.string()`, so a missing value coerces to
   * the literal `"undefined"` and sails past `.min(1)`. That was masked
   * while the person fields were required — a caller sending nothing got
   * a validation error from those instead. Now that name + roles are the
   * only requirements, this field is the guard, and a CRM row named
   * "undefined" is the exact failure it has to catch.
   */
  clientName: z
    .string()
    .pipe(nameSchema)
    .describe("Display name of the client (e.g. 'Amazon', 'Tesla', 'Walmart Logistics')."),
  personName: optionalStringSchema(nameSchema).describe(
    "Initial primary-contact person's name. Optional.",
  ),
  /**
   * Blank-tolerant but still normalized — `optionalSparseSchema` keeps
   * `phoneSchema`'s transform, so `"055 899 2922"` lands as
   * `"966558992922"` and the `(phone, org)` Contact dedup keeps working.
   */
  personPhone: optionalSparseSchema(phoneSchema).describe(
    "Initial primary-contact person's phone. Optional.",
  ),
  personEmail: optionalStringSchema(emailSchema).describe(
    "Initial primary-contact person's email. Optional.",
  ),
  roles: z
    .array(partyRoleSchema)
    .min(1)
    .describe(
      "Network roles this client plays. Multi-valued — at least one required. A single Client can be 'business_customer', 'carrier_company', 'broker', and/or 'individual_customer' simultaneously (network model, see ADR-013).",
    ),
  /**
   * Commercial registration / unified establishment number, stored as
   * `legal.registrationId`.
   *
   * Flat rather than a nested `legal` object because this is the one
   * legal field a create flow ever has: it is what Bayan stamps as
   * `carrier.moi` when this Client is the carrier named on a government
   * waybill. Everything else on `legal` (licences, tax, CR image) is
   * filled in later through the update endpoint.
   *
   * Must be 10 digits starting with 7 when present. Only meaningful for
   * a company — an individual customer has none.
   */
  crNumber: optionalStringSchema(idSchema).describe(
    'Unified establishment number (الرقم الموحد — 10 digits starting with 7) of the ' +
      'client company. Optional. Required in practice only when this client is a ' +
      'CARRIER whose waybills you will issue — it is the number the government ' +
      'document declares as the carrier. Never ask a customer (non-carrier) for it.',
  ),
  /**
   * Optional shared identity link picked from the unified
   * Profile + Org search at create time.
   *
   * - `target: 'profile'` — Brand Directory entry (pre-signup anchor).
   * - `target: 'org'`     — already-signed-up Hero Org (skip the
   *                          Profile detour).
   * - omitted              — unlinked Client (ad-hoc one-off).
   *
   * The server backfills the full {@link partySchema} snapshot via
   * `PartyResolverService.makeParty`; clients are NOT required to
   * pre-fill `ids[]` / `roles[]` — passing `{ _id, name, target }` is
   * enough.
   */
  linkedParty: partySchema.nullable().optional(),
});
export type ClientCreateAPI = API<
  z.infer<typeof clientCreateAPISchema>,
  /**
   * Returns the freshly-created `Client` doc. The primary contact is
   * carried inline on `Client.primaryContact`; a separate `Contact`
   * row is no longer auto-created here (callers that want a
   * standalone Contact entry call the `contacts.createOne` endpoint).
   */
  Client
>;

//* update api
//
// Patch shape — every field is optional so callers can send only the
// changed slice (matches the workspace's mixed inline-edit + section-modal
// UX). Sub-documents like `primaryContact` accept partial replacements
// and are merged field-by-field on the server; `legal` / `settings` /
// `linkedParty` are full sub-doc replacements.
export const clientUpdateAPISchema = clientCreateAPISchema
  .omit({
    personName: true,
    personPhone: true,
    personEmail: true,
    // Updates set the CR through the full `legal` sub-doc below. Keeping
    // the create-only shorthand here too would give callers two spellings
    // for one field, and the patch builder only honours `legal`.
    crNumber: true,
  })
  .partial()
  .extend({
    /** the id of the client to update */
    _id: schema.shape._id,
    /** Optional CRM-local domain (e.g. amazon.com). `''` clears it. */
    domain: schema.shape.domain,
    /** Free-form AI prompt / instructions for this client. `''` clears it. */
    skill: schema.shape.skill,
    /**
     * Partial primary-contact replacement; fields are merged server-side
     * as a dotted `$set`, so this is also how a contact-less Client
     * first grows a contact (one inline field at a time). Uses the
     * model's own `clientContactSchema` so the wire can't accept a shape
     * the document forbids.
     */
    primaryContact: clientContactSchema.optional(),
    /** Full legal sub-doc replacement (all inner fields optional). */
    legal: legalEntitySchema.optional(),
    /** Full settings sub-doc replacement. */
    settings: clientSettingsSchema.optional(),
    /**
     * Shared identity link replacement. Pass `null` to unlink, a
     * full Party snapshot to link/relink. Accepts both `target:
     * 'profile'` (Brand Directory entry) and `target: 'org'`
     * (signed-up Hero Org).
     */
    linkedParty: partySchema.nullable().optional(),
  });
export type ClientUpdateAPI = API<z.infer<typeof clientUpdateAPISchema>, Client>;

// * get one api
export const clientGetOneSchemaAPI = schema.pick({ _id: true });
export type ClientGetOneAPI = API<z.infer<typeof clientGetOneSchemaAPI>, Client>;

/**
 * `GET /clients/lookup-by-link` — find an existing Client in the
 * caller's own Org that already links to a given Profile / Org id.
 *
 * Used by the Client-create picker to gate duplicate creation: if a
 * Client already exists for the picked identity, the picker switches
 * to an "Open existing Client" deep link instead of POSTing a
 * duplicate.
 *
 * Either `profileId` or `orgId` (or both) must be supplied — the
 * server treats them as an `$in` match against
 * `Client.linkedParty._id` scoped to the caller's Org.
 */
export const clientLookupByLinkAPISchema = z
  .object({
    /** Profile id from the unified picker — matches `Client.linkedParty._id` when `linkedParty.target === 'profile'`. */
    profileId: objectIdSchema.optional(),
    /** Org id from the unified picker — matches `Client.linkedParty._id` when `linkedParty.target === 'org'`. */
    orgId: objectIdSchema.optional(),
  })
  .refine(v => Boolean(v.profileId) || Boolean(v.orgId), {
    message: 'profileId or orgId is required',
  });
export type ClientLookupByLinkAPI = API<z.infer<typeof clientLookupByLinkAPISchema>, Client | null>;

//* delete
export const clientDeleteAPISchema = schema.pick({ _id: true });
export type ClientDeleteAPI = API<z.infer<typeof clientDeleteAPISchema>, undefined>;

//* admin get all clients with optional filters
export const clientGetAllAdminAPISchema = paginationWithSearchSchema.extend({
  /** Optional: filter by organization ID */
  orgId: objectIdSchema.optional(),
  /** Optional: filter by a single role */
  role: partyRoleSchema.optional(),
});
export type ClientGetAllAdminAPI = API<z.infer<typeof clientGetAllAdminAPISchema>, Client[]>;

export const clientCreateAdminAPISchema = clientCreateAPISchema.extend({
  /** the organization ID */
  orgId: objectIdSchema,
});
export type ClientCreateAdminAPI = API<z.infer<typeof clientCreateAdminAPISchema>, Client>;

/**
 * `Contact` re-export so legacy callers that imported `Contact` from
 * here continue to work during the cutover. Eventually the contact API
 * lives in `contact.api.ts` only.
 */
export type { Contact };
